// Wire-format Zod schemas for media-svc HTTP endpoints.
//
// The contract is intentionally narrow: the client tells media-svc what
// it wants to upload (content-type + size); the service responds with a
// time-limited presigned URL plus the headers the client must replay
// verbatim on the PUT. The client uploads bytes directly to S3/R2/MinIO
// — media-svc never sees them.
import { z } from "zod";
// Allowlist intentionally conservative for v1: photos, voice memos, the
// occasional PDF. Adding new types is a config change, not a code one.
export const mediaContentTypeSchema = z.enum([
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
    "audio/mpeg",
    "audio/mp4",
    "audio/webm",
    "audio/ogg",
    "audio/wav",
    "application/pdf",
]);
export const presignUploadRequestSchema = z.object({
    contentType: mediaContentTypeSchema,
    // Required so the presigner can bind a Content-Length header to the
    // signature — that's what stops a smuggling attack where the client
    // signs a small upload and sends a big one.
    contentLength: z.number().int().positive(),
});
export const presignUploadResponseSchema = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string(),
    publicUrl: z.string().url(),
    expiresAt: z.string().datetime(),
    // Headers the client MUST replay byte-for-byte on the PUT.
    requiredHeaders: z.record(z.string(), z.string()),
});
// ADR-0157 — objetos del bucket privado (sin lectura anónima). La forma
// de la clave ES la marca: `org/<orgId>/private/<kind>/<scopeId>/<uuid>.<ext>`
// vive en el bucket privado; cualquier otra clave, en el público. No hay
// columna ni migración: la fila no puede contradecir al objeto, y la
// misma cadena que se firma es la que dice dónde vive. media-svc acuña la
// clave (el cliente sólo elige `kind` y `scopeId`), core-api sólo registra
// claves con esta forma y elige la ruta de lectura con isPrivateObjectKey.
const UUID_PATTERN = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
/** Qué se guarda en el bucket privado. Los que vienen (fotos y PDF del
 *  chat, acta firmada) suman una entrada acá. `downloadFilename` es el
 *  nombre fijo, en ASCII, que el GET firmado devuelve en
 *  Content-Disposition: no expone el título en la URL. */
export const PRIVATE_OBJECT_KINDS = {
    documents: { contentTypes: ["application/pdf"], downloadFilename: "documento.pdf" },
    announcements: { contentTypes: ["application/pdf"], downloadFilename: "adjunto.pdf" },
    // ADR-0158 — fotos y PDF del chat de la consulta. scopeId = inquiryId.
    inquiries: {
        contentTypes: ["image/jpeg", "image/png", "image/webp", "application/pdf"],
        downloadFilename: "adjunto.pdf",
    },
};
export const PRIVATE_OBJECT_KIND_VALUES = Object.keys(PRIVATE_OBJECT_KINDS);
// ADR-0158 — la extensión de la clave sale del Content-Type firmado en la
// subida, y al revés: el GET firmado devuelve el tipo que dice la
// extensión. Una tabla para las dos direcciones.
export const PRIVATE_OBJECT_EXTENSION_BY_CONTENT_TYPE = {
    "application/pdf": "pdf",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
};
export const PRIVATE_OBJECT_CONTENT_TYPE_BY_EXTENSION = Object.fromEntries(Object.entries(PRIVATE_OBJECT_EXTENSION_BY_CONTENT_TYPE).map(([ct, ext]) => [ext, ct]));
/** Las extensiones que puede llevar una clave de `kind`: las de sus
 *  `contentTypes`. documents y announcements sólo `pdf`. */
export function privateObjectExtensions(kind) {
    return PRIVATE_OBJECT_KINDS[kind].contentTypes.map((ct) => PRIVATE_OBJECT_EXTENSION_BY_CONTENT_TYPE[ct]);
}
// Sin distinguir mayúsculas a propósito: media-svc acuña en minúsculas,
// pero para RECHAZAR (rutas públicas, evidencias) una clave con forma
// privada en cualquier caja ya cuenta como privada.
const PRIVATE_KEY_PREFIX = new RegExp(`^org/${UUID_PATTERN}/private/`, "i");
/** ¿Vive esta clave en el bucket privado? El único predicado: lo usan
 *  media-svc (para rechazarla en las rutas públicas) y core-api (para
 *  elegir la ruta de lectura y para no componer nunca su URL pública). */
export function isPrivateObjectKey(key) {
    return PRIVATE_KEY_PREFIX.test(key);
}
/** La forma exacta de una clave privada de `orgId` (y, si se pasan, de
 *  un `kind` y un `scopeId`). La extensión depende del kind (ADR-0158):
 *  documents y announcements sólo .pdf; inquiries .jpg/.png/.webp/.pdf. */
export function privateObjectKeyRegex(orgId, kind, scopeId) {
    const kinds = kind ? [kind] : PRIVATE_OBJECT_KIND_VALUES;
    // La clave se acuña en minúsculas (media-svc normaliza orgId y scopeId),
    // así que un id en mayúsculas que llega por la ruta se compara igual.
    const scope = scopeId?.toLowerCase() ?? UUID_PATTERN;
    // Una rama por kind, cada una con sus extensiones: `documents/…/x.jpg`
    // no calza aunque `inquiries` acepte .jpg.
    const branches = kinds
        .map((k) => `${k}/(${scope})/${UUID_PATTERN}\\.(?:${privateObjectExtensions(k).join("|")})`)
        .join("|");
    return new RegExp(`^org/${orgId.toLowerCase()}/private/(?:${branches})$`);
}
/** El `kind` de una clave privada, o null si no tiene esa forma. */
export function privateObjectKindOf(key) {
    const match = new RegExp(`^org/${UUID_PATTERN}/private/([a-z_]+)/`).exec(key);
    const kind = match?.[1];
    // Object.hasOwn y no `in`: `in` también acepta lo heredado del
    // prototipo (`constructor`, `__proto__`), que el patrón deja pasar.
    return kind !== undefined && Object.hasOwn(PRIVATE_OBJECT_KINDS, kind)
        ? kind
        : null;
}
/** Lo que devuelve el GET firmado de una clave privada: el Content-Type
 *  que dice su extensión (si el kind lo admite) y el nombre fijo en ASCII.
 *  Fuente única del encabezado firmado (ADR-0158): documents y
 *  announcements responden lo mismo que antes (`application/pdf` con su
 *  `downloadFilename`). null si la clave no es privada o la extensión no
 *  corresponde al kind. */
export function privateObjectResponseOf(key) {
    const kind = privateObjectKindOf(key);
    if (kind === null)
        return null;
    const ext = /\.([a-z0-9]+)$/.exec(key)?.[1];
    if (ext === undefined || !Object.hasOwn(PRIVATE_OBJECT_CONTENT_TYPE_BY_EXTENSION, ext))
        return null;
    const contentType = PRIVATE_OBJECT_CONTENT_TYPE_BY_EXTENSION[ext];
    if (!PRIVATE_OBJECT_KINDS[kind].contentTypes.includes(contentType)) {
        return null;
    }
    const downloadFilename = ext === "pdf" ? PRIVATE_OBJECT_KINDS[kind].downloadFilename : `foto.${ext}`;
    return { kind, contentType, downloadFilename };
}
/** El presign de subida al bucket privado: igual al público, sin
 *  `publicUrl` (un objeto privado no tiene URL pública). */
export const privatePresignUploadResponseSchema = presignUploadResponseSchema.omit({
    publicUrl: true,
});
//# sourceMappingURL=media.js.map